// Registries — master admin screen.
//
// Manages container-registry credentials (URL + username + password + email +
// human name). master-admin materializes them as a single combined
// `shoptefy-pull` imagePullSecret into EVERY tenant namespace and patches the
// default ServiceAccount so pods auto-pull private images (e.g. EQ from Harbor).
//
// The password is WRITE-ONLY: the API never returns it (masked as "********"),
// and editing without re-entering it keeps the existing one.

import { useEffect, useRef, useState } from 'react';
import { I } from '../components/icons';
import { Modal, useToast, Empty } from '../components/ui';
import {
  fetchRegistries, createRegistry, updateRegistry, deleteRegistry,
  ApiError, type Registry, type RegistryType,
} from '../api/client';

const REGISTRY_TYPES: { value: RegistryType; label: string }[] = [
  { value: 'harbor', label: 'Harbor' },
  { value: 'oci', label: 'Generic OCI' },
  { value: 'gitlab', label: 'GitLab' },
];

export function RegistriesScreen() {
  const toast = useToast();
  const [registries, setRegistries] = useState<Registry[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [q, setQ] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [editFor, setEditFor] = useState<Registry | null>(null);
  const [deleteFor, setDeleteFor] = useState<Registry | null>(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      setRegistries(await fetchRegistries());
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = registries.filter((r) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return r.name.toLowerCase().includes(needle)
      || r.url.toLowerCase().includes(needle)
      || r.username.toLowerCase().includes(needle);
  });

  return (
    <div className="page-narrow">
      <div className="page-head spread">
        <div>
          <h1 className="page-title">Registries</h1>
          <p className="page-sub">
            Private container-registry credentials. master-admin materializes these as a combined
            {' '}<span className="mono">shoptefy-pull</span> imagePullSecret in every tenant namespace and wires the
            default ServiceAccount so pods auto-pull private images. Passwords are write-only — never shown again.
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setAddOpen(true)}>
          <I.Plus size={13}/> New registry
        </button>
      </div>

      <div className="metric-grid" style={{ marginBottom: 14 }}>
        <div className="metric">
          <div className="metric-label">Registries</div>
          <div className="metric-value">{registries.length}</div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="row" style={{ gap: 10 }}>
            <div style={{ position: 'relative', width: 280 }}>
              <I.Search size={13} style={{ position: 'absolute', left: 9, top: 7, color: 'var(--text-3)' }}/>
              <input className="input" placeholder="Search name, url, username…"
                     style={{ paddingLeft: 30, height: 28, fontSize: 12 }}
                     value={q} onChange={(e) => setQ(e.target.value)}/>
            </div>
          </div>
          <span className="muted" style={{ fontSize: 12 }}>
            {loading ? 'loading…' : `${filtered.length} shown`}
          </span>
        </div>

        {err && <div className="field-error" style={{ padding: 12 }}>Failed to load: {err}</div>}

        {!loading && filtered.length === 0 && (
          <Empty title="No registries" message="Add one with the button above to wire private image pulls."/>
        )}

        {filtered.length > 0 && (
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 180 }}>Name</th>
                <th>Registry URL</th>
                <th style={{ width: 100 }}>Type</th>
                <th style={{ width: 160 }}>Username</th>
                <th style={{ width: 180 }}>Email</th>
                <th style={{ width: 110 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td><span className="mono" style={{ fontSize: 12, color: 'var(--text-1)' }}>{r.url}</span></td>
                  <td>{REGISTRY_TYPES.find((t) => t.value === r.type)?.label ?? r.type}</td>
                  <td><span className="mono" style={{ fontSize: 12 }}>{r.username}</span></td>
                  <td className="muted" style={{ fontSize: 11.5 }}>{r.email || '—'}</td>
                  <td>
                    <div className="row" style={{ gap: 4 }}>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Edit"
                              onClick={() => setEditFor(r)}>
                        <I.Settings size={12}/>
                      </button>
                      <button className="btn btn-ghost btn-icon btn-sm" title="Delete"
                              onClick={() => setDeleteFor(r)}>
                        <I.Trash size={12}/>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RegistryModal open={addOpen} registry={null}
                     onClose={() => setAddOpen(false)}
                     onSaved={async () => { setAddOpen(false); await reload(); toast?.success('Registry created'); }}/>

      <RegistryModal open={editFor !== null} registry={editFor}
                     onClose={() => setEditFor(null)}
                     onSaved={async () => { setEditFor(null); await reload(); toast?.success('Registry updated'); }}/>

      <Modal open={deleteFor !== null}
             onClose={() => setDeleteFor(null)}
             title="Delete registry?"
             subtitle="The credential is removed from every tenant's pull secret. Pods already pulled keep running; new pulls of its images will fail."
             footer={
               <>
                 <button className="btn" onClick={() => setDeleteFor(null)}>Cancel</button>
                 <button className="btn btn-danger-solid" onClick={async () => {
                   if (!deleteFor) return;
                   try {
                     await deleteRegistry(deleteFor.id);
                     setDeleteFor(null);
                     await reload();
                     toast?.success('Registry deleted');
                   } catch (e) {
                     toast?.error('Delete failed', e instanceof ApiError ? e.message : String(e));
                   }
                 }}>
                   <I.Trash size={13}/> Delete
                 </button>
               </>
             }>
        {deleteFor && (
          <div className="mono" style={{ fontSize: 12, padding: 10, background: 'var(--bg-2)', borderRadius: 6 }}>
            {deleteFor.name} — {deleteFor.url}
          </div>
        )}
      </Modal>
    </div>
  );
}

// ─── Create / edit modal ─────────────────────────────────────────────────────

function RegistryModal({
  open, registry, onClose, onSaved,
}: {
  open: boolean;
  registry: Registry | null; // null = create
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const toast = useToast();
  const editing = registry !== null;

  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [type, setType] = useState<RegistryType>('harbor');
  const [submitting, setSubmitting] = useState(false);

  // Seed the form only on the closed→open transition — NOT on every re-render.
  // `registry` is a stable state ref today (no poll on this screen), but keying
  // the seed on it would wipe in-progress edits if a background refresh were ever
  // added. The ref guard makes reopening re-seed while a re-render does not.
  // Password always starts blank — it's write-only, so we never receive the real value.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setName(registry?.name ?? '');
      setUrl(registry?.url ?? '');
      setUsername(registry?.username ?? '');
      setEmail(registry?.email ?? '');
      setType(registry?.type ?? 'harbor');
      setPassword('');
      setSubmitting(false);
    }
    wasOpen.current = open;
  }, [open, registry]);

  const canSubmit = name.trim() && url.trim() && username.trim() && (editing || password);

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (editing && registry) {
        await updateRegistry(registry.id, {
          name: name.trim(),
          url: url.trim(),
          username: username.trim(),
          email: email.trim(),
          type,
          // Only send the password when the admin actually typed a new one.
          ...(password ? { password } : {}),
        });
      } else {
        await createRegistry({
          name: name.trim(),
          url: url.trim(),
          username: username.trim(),
          password,
          email: email.trim() || undefined,
          type,
        });
      }
      await onSaved();
    } catch (e) {
      toast?.error(editing ? 'Update failed' : 'Create failed', e instanceof ApiError ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}
           title={editing ? 'Edit registry' : 'New registry'}
           subtitle="Credentials are encrypted at rest and materialized into every tenant namespace as a pull secret."
           footer={
             <>
               <button className="btn" onClick={onClose}>Cancel</button>
               <button className="btn btn-primary" disabled={submitting || !canSubmit} onClick={submit}>
                 {submitting ? 'Saving…' : (editing ? 'Save' : 'Create')}
               </button>
             </>
           }>
      <div className="col" style={{ gap: 12 }}>
        <div className="field">
          <label className="field-label">Name <span className="req-dot">*</span></label>
          <input className="input" placeholder="e.g. Harbor (EQ)"
                 value={name} onChange={(e) => setName(e.target.value)}/>
        </div>
        <div className="field">
          <label className="field-label">Registry URL <span className="req-dot">*</span></label>
          <input className="input input-mono" placeholder="registry.shoptefy.com"
                 value={url} onChange={(e) => setUrl(e.target.value)}/>
          <div className="field-hint">Host only — the docker registry server, e.g. <span className="mono">registry.shoptefy.com</span>.</div>
        </div>
        <div className="field">
          <label className="field-label">Type</label>
          <select className="select" value={type} onChange={(e) => setType(e.target.value as RegistryType)}>
            {REGISTRY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div className="field-hint">Provider kind — for your reference only; every provider uses the same OCI v2 tag-listing API.</div>
        </div>
        <div className="field">
          <label className="field-label">Username <span className="req-dot">*</span></label>
          <input className="input input-mono" placeholder="robot$eq-puller"
                 value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off"/>
        </div>
        <div className="field">
          <label className="field-label">
            Password {editing ? <span className="muted" style={{ fontWeight: 400 }}>(leave blank to keep current)</span> : <span className="req-dot">*</span>}
          </label>
          <input className="input input-mono" type="password"
                 placeholder={editing ? '••••••••' : 'registry password / token'}
                 value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password"/>
          <div className="field-hint">Write-only — stored encrypted, never shown again.</div>
        </div>
        <div className="field">
          <label className="field-label">Email (optional)</label>
          <input className="input" placeholder="ops@shoptefy.com"
                 value={email} onChange={(e) => setEmail(e.target.value)}/>
        </div>
      </div>
    </Modal>
  );
}
